import React from "react";
import Button from "@mui/material/Button";

/**
 * Reusable styled button using app theme classes.
 *
 * Props:
 *   onClick    - click handler
 *   variant    - "primary" | "secondary" | "outline" | "fill" — default "primary"
 *                  primary   → submit_btn (solid brand background)
 *                  secondary → cancel_btn (bordered, brand text)
 *                  outline   → transparent bg, brand-colored border + text
 *                  fill      → explicit solid brand fill (no class dependency)
 *   icon       - optional React node rendered before children
 *   children   - button label
 *   disabled   - boolean
 *   type       - button type (default "button")
 *   style      - extra inline styles
 *   title      - tooltip
 *   className  - optional extra class names appended to the variant's base class
 *   autoFocus  - boolean, focuses the button on mount
 */

const buildVariantStyles = (variant, fillColor) => {
  const color = fillColor || "var(--app-button-color)";
  const gradient = fillColor
    ? color
    : "var(--app-ai-assistant-gradient, linear-gradient(135deg,#253978,#1a2a5e))";
  switch (variant) {
    case "secondary": 
      return { 
        className: "", 
        inline: { 
          backgroundColor: "var(--btn-secondary-bg, #fff)",
          color: "var(--btn-secondary-color, var(--app-button-color))",
          border: "var(--btn-secondary-border, 1px solid var(--app-button-color))",
        } 
      };
    case "outline":   
      return { 
        className: "", 
        inline: { 
          background: "var(--btn-outline-bg, transparent)", 
          border: `var(--btn-outline-border, 1.5px solid ${color})`, 
          color: `var(--btn-outline-color, ${color})` 
        } 
      };
    case "fill":      
      return { 
        className: "", 
        inline: { 
          background: gradient, 
          border: "none", 
          color: "#fff" 
        } 
      };
    default:          
      return { 
        className: "", 
        inline: { 
          backgroundColor: "var(--btn-primary-bg, var(--app-button-color))",
          color: "var(--btn-primary-color, #fff)",
          border: "var(--btn-primary-border, none)",
        } 
      };
  }
};

const AppButton = ({
  onClick,
  variant = "primary",
  fillColor,
  icon,
  children,
  disabled = false,
  type = "button",
  style = {},
  title,
  className: extraClassName,
  autoFocus = false,
}) => {
  const { inline } = buildVariantStyles(variant, fillColor);

  const disabledInline = disabled ? {
    background: "#e5e7eb !important",
    color: "#9ca3af",
    borderColor: "#e5e7eb",
    cursor: "not-allowed",
    opacity: 1,
  } : {};

  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      autoFocus={autoFocus}
      className={extraClassName || ""}
      sx={disabled ? {
        "&.Mui-disabled": {
          background: "#e5e7eb !important",
          color: "#9ca3af !important",
          border: "1px solid #e5e7eb !important",
          cursor: "not-allowed",
          opacity: 1,
          pointerEvents: "auto",

        }
      } : {}}
      style={{
        borderRadius: "var(--btn-primary-radius, 6px)",
        cursor: disabled ? "not-allowed" : "pointer",
        width: "auto",
        textTransform: "inherit",
        padding: "8px 12px",
        fontWeight: "600",
        display: "flex",
        alignItems: "center",
        gap: icon && children ? "8px" : 0,
        transition: "all 0.3s",
        ...inline,
        ...style,
        ...disabledInline,
      }}
    >
      {icon}
      {children}
    </Button>
  );
};

export default AppButton;
